import React, { useEffect, useRef, useState } from 'react';
import { Copy, KeyRound, Smartphone, UserX } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { employeeAPI } from '../services/api';
import { confirmDialog, notify, reportLoadError } from '../utils/notify';
import StatusBadge from '../components/StatusBadge';
import './PortalAccessSection.css';

// The guard portal account of one employee, in the employee's details
// (employees:update, as the API asks). Giving access shows the username
// and a temporary password once: they are not shown again, the guard
// changes the password at the first sign-in. Revoking closes the account.

const REASONS = {
  no_code: 'Give this employee an employee code first: it is the username.',
  employee_inactive: 'Only active employees or employees on leave get portal access.',
};

const PortalAccessSection = ({ employeeId }) => {
  const { t, locale } = useLanguage();
  const [state, setState] = useState(null);
  const [created, setCreated] = useState(null); // { username, temporary_password }, shown once
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    let cancelled = false;
    setState(null);
    employeeAPI.getPortalAccess(employeeId)
      .then((res) => { if (!cancelled) setState(res.data); })
      .catch((error) => reportLoadError(error, tRef.current('Error loading portal access')));
    return () => { cancelled = true; };
  }, [employeeId, version]);

  useEffect(() => { setCreated(null); }, [employeeId]);

  const give = async () => {
    try {
      setBusy(true);
      const res = await employeeAPI.givePortalAccess(employeeId);
      setCreated({ username: res.data.username, password: res.data.temporary_password });
      setVersion((n) => n + 1);
    } catch (error) {
      notify(error.response?.data?.message || t('Error giving portal access'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!await confirmDialog(t('Revoke portal access? The guard is signed out and cannot sign in again until access is given again.'))) return;
    try {
      setBusy(true);
      await employeeAPI.revokePortalAccess(employeeId);
      setCreated(null);
      notify(t('Portal access revoked'), 'success');
      setVersion((n) => n + 1);
    } catch (error) {
      notify(error.response?.data?.message || t('Error revoking portal access'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${t('Username')}: ${created.username}\n${t('Temporary password')}: ${created.password}`);
      notify(t('Copied'), 'success');
    } catch (e) {
      notify(t('Could not copy: select the text instead'), 'warning');
    }
  };

  const account = state && state.account;
  const lastLogin = account && account.last_login
    ? new Date(`${account.last_login}:00`).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <section className="emp-section pa-section">
      <div className="emp-section__head">
        <h3 className="emp-section__title"><Smartphone aria-hidden="true" /> {t('Guard Portal')}</h3>
        {account && (
          <button type="button" className="btn btn--secondary" onClick={revoke} disabled={busy}>
            <UserX aria-hidden="true" /> {t('Revoke access')}
          </button>
        )}
        {state && state.can_give && (
          <button type="button" className="btn btn--primary" onClick={give} disabled={busy}>
            <KeyRound aria-hidden="true" /> {t('Give portal access')}
          </button>
        )}
      </div>

      {!state ? (
        <p className="emp-no-data">{t('Loading...')}</p>
      ) : account ? (
        <dl className="emp-detail-grid">
          <div className="emp-detail-item">
            <dt>{t('Username')}</dt>
            <dd className="pa-mono">{account.username}</dd>
          </div>
          <div className="emp-detail-item">
            <dt>{t('Status:')}</dt>
            <dd>
              <StatusBadge status={account.status}>{account.status === 'active' ? t('Active') : t('Inactive')}</StatusBadge>
              {account.must_change_password && <span className="pa-note"> {t('Must change the password at the next sign-in')}</span>}
            </dd>
          </div>
          <div className="emp-detail-item">
            <dt>{t('Last sign-in')}</dt>
            <dd>{lastLogin || t('Never')}</dd>
          </div>
        </dl>
      ) : (
        <p className="emp-no-data">{state.reason ? t(REASONS[state.reason]) : t('No portal access yet')}</p>
      )}

      {created && (
        <div className="pa-created" role="status">
          <p className="pa-created__title">{t('Give these to the guard now: the password is not shown again.')}</p>
          <dl className="pa-created__grid">
            <dt>{t('Username')}</dt>
            <dd className="pa-mono">{created.username}</dd>
            <dt>{t('Temporary password')}</dt>
            <dd className="pa-mono">{created.password}</dd>
          </dl>
          <p className="pa-note">{t('The guard signs in at the same address as everyone else and must choose a new password.')}</p>
          <div className="pa-created__actions">
            <button type="button" className="btn btn--secondary" onClick={copy}><Copy aria-hidden="true" /> {t('Copy')}</button>
            <button type="button" className="btn btn--secondary" onClick={() => setCreated(null)}>{t('Done')}</button>
          </div>
        </div>
      )}
    </section>
  );
};

export default PortalAccessSection;
